import { Component, Input, inject, HostListener } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, take, map } from 'rxjs';
import { MedicationRequest, Bundle, MedicationDispense, Dosage } from 'fhir/r4';

import { MedicationRequestSearchService, MedicationRequestRow } from './medication-request-search.service';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { commonImports } from '../shared/table-interface';
import { UtilityService } from '../shared/utility.service';
import { LinkInReferencesService } from '../shared/link-in-references.service';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { StateService } from '../shared/state.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { ErrorService } from '../shared/error.service';
import { RequestPrintService } from '../shared/request-print.service';
import { MedicationDispenseUtilityService } from '../medication-dispense/medication-dispense-utility.service';
import { RequestsHelperService } from './requests-helper.service';
import { MedicineRequestsDialogService } from './medicine-requests-dialog.service';
import { MedicineAdministrationDialogService } from './medicine-administration-dialog.service';
import { ResourceRefreshService } from '../shared/resource-refresh.service';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { MessagingThreadsService } from '../messaging/messaging-threads.service';
import { MessagingParticipant, MessagingSubject, EnsureThreadResponse } from '../messaging/messaging.models';
import { DetailzViewzComponent, DetailActionButton } from '../detailz-viewz/detailz-viewz.component';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { PriceDisplayComponent } from '../shared/price-display/price-display.component';
import { backendEndPointToken } from '../app.config';

import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
// Note: removed unused template imports to fix compile warnings
import { EmptyStateComponent } from "../shared/empty-state/empty-state.component";
import { MatTableModule } from '@angular/material/table';

@Component({
  selector: 'app-medicine-requests',
  imports: [
    ...commonImports,
    CommonModule,
    MatProgressSpinnerModule,

    EmptyStateComponent,
    TableHeaderComponent,
    MatPaginatorModule,
    MatTableModule
  ],
  templateUrl: './medicine-requests.component.html',
  styleUrl: './medicine-requests.component.scss'
})
export class MedicineRequestsComponent {
  statusStyles = baseStatusStyles;
  islink = inject(LinkInReferencesService);
  canPrescribeMedications$: Observable<boolean> = new BehaviorSubject<boolean>(false);

  utilityService = inject(UtilityService);
  searchableFields = [
    { label: 'Patient ID', value: 'subject:Patient.identifier' },
    { label: 'Medication Name (In-Stock-Referenced)', value: 'medication-in-stock' },
    { label: 'Medication Name (Not Referenced)', value: 'medication-not-in-stock' }
  ];
  selectedSearchField = 'subject:Patient.identifier';
  route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private errorService = inject(ErrorService);
  private http = inject(HttpClient);
  private backendEndPoint = inject(backendEndPointToken);
  private requestPrint = inject(RequestPrintService);
  private medicationDispenseUtilityService = inject(MedicationDispenseUtilityService);
  private requestsHelperService = inject(RequestsHelperService);
  medicineRequestsDialogService = inject(MedicineRequestsDialogService);
  medicineAdministrationDialogService = inject(MedicineAdministrationDialogService);
  encounterService = inject(EncounterServiceService);
  stateService = inject(StateService);
  private resourceRefresh = inject(ResourceRefreshService);
  private messagingThreadsService = inject(MessagingThreadsService);
  auth = inject(AuthService);

  private searchService = inject(MedicationRequestSearchService);
  tableDataSource = this.searchService.dataSource;
  loading$ = this.searchService.loading$;
  total$ = this.searchService.total$;
  error$ = this.searchService.error$;

  canUpdate$!: Observable<boolean>;
  canDispense$!: Observable<boolean>;
  canAdminister$!: Observable<boolean>;
  isPractitioner$!: Observable<boolean>;

  patientId!: string | null;
  user: any = null;
  capacityObject = capacityObject;

  sortedWithRequisition: { [key: string]: MedicationRequestRow[] } = {};
  sortedWithRequisitionKeys: string[] = [];

  tableFilter = new Map([
    ['status', ['active', 'on-hold', 'ended', 'stopped', 'completed', 'cancelled', 'draft']]
  ]);
  tableFilterFormControlObject: { [key: string]: FormGroup } = {};
  pageSize = 25;
  paginatorPageIndex = 0;

  tableColumns!: string[];
  private readonly baseTableColumns = [
    'groupIdentifier',
    'status',
    'subject',
    'medication',
    'action',
    'groupactions'
  ];
  // resourceRefresh = inject(ResourceRefreshService);

  private desktopColumns: string[] = [...this.baseTableColumns];
  private mobileColumns: string[] = ['status', 'medication', 'action', 'groupactions'];

  @Input() set medicineRequestTableColumns(columns: string[] | undefined) {
    this.tableColumns = columns?.length ? [...columns] : [...this.baseTableColumns];
  }

  detailsBuilder: DetailsBuilderObject = {
    resourceName: 'Medication Request',
    resourceIcon: 'medications',
    specialHeader: {
      strongSectionKey: ['medicationCodeableConcept', 'medicationReference'],
      iconSectionKeys: ['status', 'intent'],
      contentSectionKeys: ['subject', 'authoredOn']
    },
    groups: [
      { groupName: 'Classification', groupIcon: 'category', groupKeys: ['priority', 'status', 'intent', 'category'] },
      { groupName: 'Participants', groupIcon: 'group', groupKeys: ['subject', 'requester', 'performer', 'performerType'] },
      { groupName: 'Clinical Details', groupIcon: 'science', groupKeys: ['medicationCodeableConcept', 'medicationReference', 'dosageInstruction', 'reasonCode'] },
      { groupName: 'Fulfilment', groupIcon: 'check_circle', groupKeys: ['authoredOn', 'dispenseRequest', 'substitution'] }
    ]
  };

  ngOnInit() {
    this.auth.user.subscribe((user) => {
      this.user = user;
    });

    this.canPrescribeMedications$ = this.auth.user.pipe(
      map(() => this.auth.canPrescribeMedications())
    );

    this.canUpdate$ = this.auth.user.pipe(
      map(() => this.auth.can('medicationRequest', 'update'))
    );

    this.canDispense$ = this.auth.user.pipe(
      map(() => this.auth.can('medicationRequest', 'dispense'))
    );

    this.canAdminister$ = this.auth.user.pipe(
      map(() => this.auth.can('medicationRequest', 'administer'))
    );

    this.isPractitioner$ = this.auth.user.pipe(
      map(user => user?.accountType === 'Practitioner' || user?.role !== 'patient')
    );

    const resolvedPatientId = this.stateService.currentPatientIdFromResolver.getValue();
    const encounterPatientId = this.stateService.currentEncounter.getValue()?.patientId ?? null;
    this.patientId = resolvedPatientId ?? encounterPatientId;

    this.searchService.init(this.patientId);

    // Initialize filter form controls for app-table-header
    for (const [key, value] of this.tableFilter) {
      this.tableFilterFormControlObject[key] = new FormGroup({});
      for (const filterValue of value) {
        this.tableFilterFormControlObject[key].addControl(filterValue, new FormControl(false));
      }
    }

    // Initial columns setup
    if (!this.tableColumns) {
      this.tableColumns = [...this.baseTableColumns];
    }
    if (this.patientId && this.tableColumns.includes('subject')) {
      this.tableColumns = this.tableColumns.filter(c => c !== 'subject');
    }

    // Monitor loaded rows to apply grouping
    this.searchService.dataSource.connect().subscribe(rows => {
      this.applyGrouping(rows as MedicationRequestRow[]);
    });

    // Responsive columns
    this.updateDisplayedColumns();

    // Subscribe to external refresh requests

    this.resourceRefresh.onRefresh('medicine-requests').subscribe((opts: any) => {
// $#$      // /* alert(String(this.patientId) */ + JSON.stringify(opts));
      try {
        if (opts?.full) {
          this.searchService.init(this.patientId);
// $#$          // /* alert('Full refresh triggered for medicine requests.'); */
        } else {
          this.refresh();
        }
      } catch (e) {
        this.refresh();
      }
    });

    // Handle initial route-based dialog if any
    this.route.queryParamMap.pipe(take(1)).subscribe(params => {
      const id = params.get('medRequestId');
      if (id) {
        this.fetchAndShowRow(id);
      }
    });
  }

  private applyGrouping(rows: MedicationRequestRow[]) {
    const groups: { [key: string]: MedicationRequestRow[] } = {};
    rows?.forEach(row => {
      const gid = row.groupIdentifier || row.id || 'N/A';
      if (!groups[gid]) groups[gid] = [];
      groups[gid].push(row);
    });

    const keys = Object.keys(groups).sort((a, b) => {
      if (a === 'N/A') return 1;
      if (b === 'N/A') return -1;
      return a.localeCompare(b);
    });

    this.sortedWithRequisition = groups;
    this.sortedWithRequisitionKeys = keys;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns(): void {
    try {
      const isMobile = window.innerWidth < 600;
      const columns = isMobile ? this.mobileColumns : this.desktopColumns;
      this.tableColumns = columns.filter(c => !(this.patientId && c === 'subject'));
    } catch (e) {
      // fallback to base columns
      this.tableColumns = [...this.baseTableColumns];
    }
  }

  onFilterChanged(event: any): void {
    const filters: any = {};
    filters[event.filterKey] = event.values.join(',');
    this.searchService.search(filters);
    this.paginatorPageIndex = 0;
  }

  onSearchCriteriaChanged(event: any): void {
    const filters: any = {};
    const field = event.field || this.selectedSearchField;
    const term = event.term;

    if (field === 'medication-in-stock') {
      // Standard FHIR 'code' search parameter for MedicationRequest handles both
      // medicationCodeableConcept and medicationReference.code.

      filters['medication.code:text'] = String(term).toLowerCase();
    } else if (field === 'medication-not-in-stock') {
      filters['code:text'] = String(term).toLowerCase();
    } else {
      filters[field] = term;
    }

    this.searchService.search(filters);
    this.paginatorPageIndex = 0;
  }

  onPageChange(event: PageEvent): void {
    this.paginatorPageIndex = event.pageIndex;
    this.searchService.changePage(event.pageIndex);
  }

  refresh(): void {
    this.searchService.refresh();
  }

  async showRow(row: MedicationRequest): Promise<void> {
    const dialogRef = this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '650px',
      disableClose: true,
      data: {
        resourceData: await this.buildDetailsBuilderForRow(row),
        detailsBuilderObject: this.detailsBuilder,
        actionButtons: await this.buildMedicationRequestActions(row)
      }
    });
    let navigatingToThread = false;

    const sub = dialogRef.componentInstance?.actionInvoked.subscribe(({ key }) => {
      if (key === 'messageRequester') {
        navigatingToThread = true;
        dialogRef.close({ navigatingToThread: true });
      } else {
        this.handleDetailAction(key, row);
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.navigatingToThread) {
        // Handle navigation inside the component's context to ensure the dialog is fully gone
        this.messageRequester(row);
        return;
      }
      // Clear the query parameter so the dialog doesn't pop open again on refresh
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { medRequestId: null },
        queryParamsHandling: 'merge'
      });
      sub?.unsubscribe();
    });

  }

  private async fetchAndShowRow(id: string) {
    try {
      const url = `${this.backendEndPoint}/MedicationRequest/${id}`;
      const resource = await firstValueFrom(this.http.get<MedicationRequest>(url));
      if (resource) {
        this.showRow(resource);
      }
    } catch (e) {
      console.error('Failed to fetch MR for route', e);
    }
  }

  handleDetailAction(actionKey: string, row: MedicationRequest): void {
    switch (actionKey) {
      case 'printMedicationRequest':
        this.requestPrint.printMedicationRequest(row);
        break;
      case 'viewDispenseDetails':
        this.viewDispenseDetails(row);
        break;
      case 'edit':
        this.encounterService.addMedicationRequest(this.patientId || '', true, row);
        break;
      case 'changeStatus':
        this.promptStatusChange(row);
        break;
      case 'recordDispense':
        this.medicineRequestsDialogService.openDispenseDialog(
          row.id ?? null,
          row.medicationReference ?? null,
          row.medicationCodeableConcept ?? null,
          row.subject ?? null,
          row
        );
        break;
      case 'recordAdministration':
        this.medicineAdministrationDialogService.openAdministrationDialog(row);
        break;
      case 'activate':
        this.activateDraftMedicationRequest(row);
        break;
      case 'messageRequester':
        this.messageRequester(row);
        break;
      default:
        break;
    }
  }

  private messageRequester(row: MedicationRequest) {
    this.encounterService.ensureMedicationRequestMessagingThread(row).subscribe({
      next: (response) => {
        if (response?.thread?.threadId) {
          this.router.navigate(['/app/messaging/thread'], {
            queryParams: { threadId: response.thread.threadId }
          });
        } else {
          this.errorService.openandCloseError('Could not resolve or create a messaging thread.');
        }
      },
      error: (err) => {
        this.errorService.openandCloseError('Failed to open messaging thread.');
      }
    });
  }

  private async viewDispenseDetails(row: MedicationRequest) {
    const mrId = row.id;
    if (!mrId) return;

    try {
      const url = `${this.backendEndPoint}/MedicationDispense?prescription=MedicationRequest/${mrId}`;
      const bundle = await firstValueFrom(this.http.get<Bundle>(url));
      const dispenses = (bundle.entry ?? []).map(e => e.resource as MedicationDispense);
      const target = dispenses.sort((a, b) => (b.whenHandedOver || '').localeCompare(a.whenHandedOver || ''))[0];

      if (target?.id) {
        this.router.navigate(['/app/medication-dispense'], { queryParams: { dispenseId: target.id } });
      } else {
        this.errorService.openandCloseError('No linked dispense record found.');
      }
    } catch (e) {
      this.errorService.openandCloseError('Error resolving linked dispense.');
    }
  }

  private activateDraftMedicationRequest(row: MedicationRequest) {
    const patch = [
      { op: 'replace', path: '/intent', value: 'order' },
      { op: 'replace', path: '/status', value: 'active' }
    ];
    this.http.patch(`${this.backendEndPoint}/MedicationRequest/${row.id}`, patch, {
      headers: { 'Content-Type': 'application/json-patch+json' }
    }).subscribe({
      next: () => {
        this.snackBar.open('Medication Request activated.', 'Close', { duration: 3000 });
        try { this.resourceRefresh.notify('medicine-requests', { full: false }); } catch (e) { this.refresh(); }
      },
      error: () => this.errorService.openandCloseError('Failed to activate request.')
    });
  }

  private promptStatusChange(row: MedicationRequest) {
    const allowed = ['cancelled', 'entered-in-error', 'stopped', 'completed'].filter(s => s !== row.status);

    const dynF = this.dialog.open(DynamicFormsV2Component, {
      width: '400px',
      data: {
        formMetaData: { formName: 'Change Status', submitText: 'Update' },
        formFields: [
          {
            generalProperties: { fieldApiName: 'status', fieldLabel: 'New Status', inputType: 'select', value: allowed[0] },
            data: allowed, dataType: 'string'
          }
        ]
      }
    });

    dynF.afterClosed().subscribe(res => {
      if (res?.values?.status) {
        this.http.patch(`${this.backendEndPoint}/MedicationRequest/${row.id}`,
          [{ op: 'replace', path: '/status', value: res.values.status }],
          { headers: { 'Content-Type': 'application/json-patch+json' } }
        ).subscribe(() => {
          this.snackBar.open('Status updated.', 'Close', { duration: 3000 });
          try { this.resourceRefresh.notify('medicine-requests', { full: false }); } catch (e) { this.refresh(); }
        });
      }
    });
  }

  printMedicationGroup(groupId: string) {
    const rows = (this.sortedWithRequisition[groupId] || []).map(r => r.rawResource as MedicationRequest);
    this.requestPrint.printMedicationRequestGroup(groupId, rows);
  }

  showPrices(groupId: string) {
    const resources = (this.sortedWithRequisition[groupId] || []).map(r => r.rawResource);
    this.dialog.open(PriceDisplayComponent, {
      width: '600px',
      data: { resourceType: 'medication', resources, showBreakdown: true }
    });
  }

  private async buildMedicationRequestActions(row: MedicationRequest): Promise<DetailActionButton[]> {
    const base: DetailActionButton[] = [
      { key: 'printMedicationRequest', label: 'Print Request', icon: 'print', color: 'primary', },
      { key: 'viewDispenseDetails', label: 'View Dispense', icon: 'local_pharmacy', color: 'primary', capabilities: [{ resource: 'medicationDispense', action: 'viewDispensed' }] }
    ];

    if (row.status === 'draft') {
      base.unshift(
        { key: 'activate', label: 'Activate', icon: 'play_arrow', color: 'primary', capabilities: [{ resource: 'medicationRequest', action: 'update' }] },
        { key: 'edit', label: 'Edit', icon: 'edit', color: 'primary', capabilities: [{ resource: 'medicationRequest', action: 'update' }] }
      );
    }

    const dispenseState = await firstValueFrom(this.requestsHelperService.getDispenseStateForRequest(row).pipe(take(1)));

    if (!dispenseState.hasDispenseInProgress && !dispenseState.isDispenseCompleted) {
      base.push({ key: 'changeStatus', label: 'Change Status', icon: 'sync', color: 'warn', capabilities: [{ resource: 'medicationRequest', action: 'update' }] });
    }

    if (dispenseState.isDispensable) {
      base.push(
        { key: 'recordDispense', label: 'Dispense', icon: 'local_pharmacy', color: 'accent', capabilities: [{ resource: 'medicationDispense', action: 'dispense' }] },
        { key: 'recordAdministration', label: 'Administer', icon: 'vaccines', color: 'primary', capabilities: [{ resource: 'medicationRequest', action: 'administer' }] }
      );
    }

    base.push({ key: 'messageRequester', label: 'Message Requester', icon: 'chat', color: 'primary' });

    return base;
  }

  private async buildDetailsBuilderForRow(row: MedicationRequest & Record<string, any>): Promise<DetailsBuilderObject> {
    const base: DetailsBuilderObject = { ...this.detailsBuilder, groups: [...(this.detailsBuilder.groups || [])] };
    const dosage: Dosage[] = row.dosageInstruction || [];

    if (dosage.length && !base.groups?.some((g: any) => g.groupName === 'Dosage Instructions')) {
      base.groups?.push({
        groupName: 'Dosage Instructions',
        groupIcon: 'schedule',
        groupKeys: this.flattenDosageInstructionKeys(dosage)
      });
    }

    const dispenseState: any = await firstValueFrom(this.requestsHelperService.getDispenseStateForRequest(row).pipe(take(1)));
    if (dispenseState?.hasDispenseInProgress) row['dispenseStatus'] = 'dispense-in-progress';
    if (dispenseState?.isDispenseCompleted) row['dispenseStatus'] = 'dispense-completed';

    if (row['dispenseStatus'] && base.specialHeader?.iconSectionKeys && !base.specialHeader.iconSectionKeys.includes('dispenseStatus')) {
      base.specialHeader.iconSectionKeys.push('dispenseStatus');
    }

    const flattened = this.flattenDosageInstructionValues(dosage);
    return { ...row, ...flattened } as any;
  }

  private flattenDosageInstructionKeys(dosage: Dosage[]): string[] {
    const keys: string[] = [];
    dosage.forEach((_, i) => {
      const n = i + 1;
      keys.push(`Dosage ${n} - Instruction`, `Dosage ${n} - Timing`, `Dosage ${n} - Timing Code`, `Dosage ${n} - Duration`, `Dosage ${n} - Dose`);
    });
    return keys;
  }

  private flattenDosageInstructionValues(dosage: Dosage[]): Record<string, any> {
    const out: Record<string, any> = {};
    dosage.forEach((d: any, i) => {
      const n = i + 1;
      const repeat = d?.timing?.repeat ?? {};
      const dq = d?.doseAndRate?.[0]?.doseQuantity;

      out[`Dosage ${n} - Instruction`] = d?.text || '';
      out[`Dosage ${n} - Timing`] = repeat.frequency ? `${repeat.frequency} time(s) every ${repeat.period} ${repeat.periodUnit || ''}` : '';
      out[`Dosage ${n} - Timing Code`] = d?.timing?.code?.text || d?.timing?.code?.coding?.[0]?.display || '';
      out[`Dosage ${n} - Duration`] = repeat.duration ? `${repeat.duration} ${repeat.durationUnit || ''}` : '';
      out[`Dosage ${n} - Dose`] = dq?.value ? `${dq.value} ${dq.unit || dq.code || ''}` : '';
    });
    return out;
  }
}
